import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function SettingsTab() {
  const { refresh } = useSite();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setForm(r.data))
      .catch((err) => console.error("Failed to load settings:", err));
  }, []);

  if (!form) return <div className="p-6">Loading...</div>;

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...rest } = form;
      await api.put("/admin/settings", rest);
      toast.success("Settings saved");
      refresh();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const label = "text-xs uppercase tracking-widest text-[#6B705C] mb-1.5 block";
  const input = "w-full bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-3 py-2.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30 text-sm";

  const fields = [
    { k: "brand", label: "Brand name" },
    { k: "phone", label: "Phone (with country code, e.g. +30 6971819748)" },
    { k: "phone_display", label: "Phone display (formatted for screen)" },
    { k: "whatsapp", label: "WhatsApp number (digits only, e.g. 306971819748)" },
    { k: "email", label: "Email address" },
    { k: "address", label: "Postal address" },
    { k: "maps_link", label: "Google Maps share link" },
    { k: "maps_embed", label: "Google Maps embed URL" },
    { k: "facebook_url", label: "Facebook URL (optional)" },
    { k: "instagram_url", label: "Instagram URL (optional)" },
    { k: "hero_image", label: "Hero image URL (full-width photo on homepage)" },
  ];

  return (
    <div className="p-6 max-w-3xl">
      <h2 className="font-serif text-2xl text-[#1A2B4C] mb-1">Site Settings</h2>
      <p className="text-sm text-[#6B705C] mb-6">
        Contact info, social links and hero image used across the website.
      </p>

      <div className="bg-white rounded-2xl border border-[#E9E2D3] p-6 space-y-4">
        {fields.map(({ k, label: lab }) => (
          <div key={k}>
            <label className={label}>{lab}</label>
            <input
              className={input}
              value={form[k] || ""}
              onChange={(e) => upd(k, e.target.value)}
              data-testid={`settings-${k}`}
            />
          </div>
        ))}
        <div className="pt-4 border-t border-[#E9E2D3]">
          <label className={label}>Default minimum stay (nights)</label>
          <input
            type="number"
            min="1"
            step="1"
            className={input + " sm:max-w-[200px]"}
            value={form.default_min_nights ?? 1}
            onChange={(e) =>
              upd(
                "default_min_nights",
                e.target.value === "" ? "" : Math.max(1, parseInt(e.target.value, 10) || 1)
              )
            }
            data-testid="settings-default_min_nights"
          />
          <p className="text-[10px] text-[#6B705C] mt-1">
            Applied to every apartment unless it defines its own minimum stay (Apartments tab → Minimum stay field).
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-settings"
          className="inline-flex items-center gap-2 bg-[#1A2B4C] text-white px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save settings"}
        </button>
      </div>
    </div>
  );
}
